import { useEffect, useState } from 'preact/hooks';
import { UploadManager } from '@brainfocus/web/utils/upload_manager';
import type { UploadProgress } from '@brainfocus/web/utils/upload_manager';
import './upload_snackbar.scss';
import { ownerWindowOf } from '@brainfocus/web/shared/platform/owner_window';

/**
 * UploadSnackbar displays active uploads at the bottom of the page
 * (except on the upload page itself)
 */
export function UploadSnackbar() {
    const [uploads, setUploads] = useState<Map<string, UploadProgress>>(new Map());

    // Subscribe to UploadManager on mount
    useEffect(() => {
        const manager = UploadManager.getInstance();
        const subscription = manager.activeUploads$.subscribe((newUploads) => {
            setUploads(new Map(newUploads));
        });

        return () => subscription.unsubscribe();
    }, []);

    // Warn user before closing/refreshing with active uploads
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
            const activeUploads = Array.from(uploads.values()).filter(
                upload => upload.status === 'uploading' || upload.status === 'processing',
            );

            if (activeUploads.length > 0) {
                // Standard way to trigger browser confirmation dialog
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set
                return ''; // Some browsers use return value
            }
            return undefined;
        };

        // The app window: the snackbar is never rendered into a detached viewer.
        const win = ownerWindowOf(null);
        win.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            win.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [uploads]);

    // Always show all uploads
    const visible = uploads.size > 0;

    const handleClose = (uploadId: string) => {
        const manager = UploadManager.getInstance();
        manager.removeUpload(uploadId);
    };

    return visible
        ? (
            <div class='upload-snackbar yellow-bg'>
                {Array.from(uploads.values()).map(upload => (
                    <div class='upload-item' key={upload.uploadId}>
                        <span class='file-name'>{upload.fileName}</span>
                        <span class='progress'>
                            {upload.status === 'failed'
                                ? `Error: ${upload.error}`
                                : upload.status === 'processing'
                                    ? `Processing... ${upload.progress}%`
                                    : `Uploading... ${upload.progress}%`}
                        </span>
                        {upload.status === 'failed' && (
                            <button
                                class='close-button'
                                onClick={() => handleClose(upload.uploadId)}
                                aria-label='Close'
                            >
                                ×
                            </button>
                        )}
                    </div>
                ))}
            </div>
            )
        : null;
}
